// Mock data for organ transport monitoring — replace with API/hardware later

/**
 * Organ-specific temperature safe ranges (°C) during transport.
 * Heart: avoid too-cold; ideal ~4–8°C.
 * Kidney/Liver/Lung/Pancreas: SCS (static cold storage) typically 0–4°C.
 */
export const ORGAN_TEMP_RANGES = {
  Heart: { min: 4, max: 8 },
  Kidney: { min: 0, max: 4 },
  Liver: { min: 0, max: 4 },
  Lung: { min: 0, max: 4 },
  Pancreas: { min: 0, max: 4 },
};

/** Default range when organ type unknown (conservative: SCS) */
export const DEFAULT_TEMP_RANGE = { min: 0, max: 4 };

/** @deprecated Use getTempRangeForOrgan instead */
export const TEMP_SAFE_RANGE = { min: 2, max: 6 };

export function getTempRangeForOrgan(organType) {
  if (!organType) return DEFAULT_TEMP_RANGE;
  return ORGAN_TEMP_RANGES[organType] ?? DEFAULT_TEMP_RANGE;
}

export function getTempStatusForOrgan(temp, organType) {
  if (temp == null) return 'ok';
  const range = getTempRangeForOrgan(organType);
  return temp >= range.min && temp <= range.max ? 'ok' : 'alert';
}

// Synthetic locations (SF Bay Area) — used when no GPS from device
const SYNTHETIC_BASE = { lat: 37.7749, lng: -122.4194 };
export function getSyntheticLocation(deviceId, index = 0) {
  const seed = (deviceId || 'DEV-001').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const t = index / 100;
  return {
    lat: SYNTHETIC_BASE.lat + Math.sin(t + seed) * 0.015 + Math.cos(t * 0.7) * 0.008,
    lng: SYNTHETIC_BASE.lng + Math.cos(t * 1.2 + seed) * 0.015 + Math.sin(t * 0.5) * 0.008,
  };
}

export const activeTransports = [
  {
    id: 'T-2847',
    organType: 'Heart',
    destination: 'Memorial Hospital',
    destLat: 37.7849,
    destLng: -122.4094,
    status: 'in_transit',
    temperature: 4.2,
    humidity: 45.4,
    tempStatus: 'ok',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T08:30:00Z',
    estimatedArrival: '2025-02-14T11:45:00Z',
    deviceId: 'DEV-001',
    lat: 37.7749,
    lng: -122.4194,
  },
  {
    id: 'T-2848',
    organType: 'Liver',
    destination: 'City Medical Center',
    destLat: 37.7799,
    destLng: -122.4150,
    status: 'in_transit',
    temperature: 5.1,
    humidity: 52.0,
    tempStatus: 'alert',
    lastShock: { g: 2.1, at: '2025-02-14T09:15:00Z' },
    shockStatus: 'warning',
    startedAt: '2025-02-14T07:00:00Z',
    estimatedArrival: '2025-02-14T10:30:00Z',
    deviceId: 'DEV-002',
    lat: 37.7821,
    lng: -122.4102,
  },
  {
    id: 'T-2849',
    organType: 'Kidney',
    destination: 'Regional Transplant Unit',
    destLat: 37.7699,
    destLng: -122.4294,
    status: 'in_transit',
    temperature: 7.8,
    humidity: 48.2,
    tempStatus: 'alert',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T09:00:00Z',
    estimatedArrival: '2025-02-14T12:00:00Z',
    deviceId: 'DEV-003',
    lat: 37.7698,
    lng: -122.4291,
  },
  {
    id: 'T-2850',
    organType: 'Kidney',
    destination: 'Stanford Medical Center',
    destLat: 37.4381,
    destLng: -122.1719,
    status: 'in_transit',
    temperature: 4.5,
    humidity: 44.0,
    tempStatus: 'alert',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T08:15:00Z',
    estimatedArrival: '2025-02-14T09:45:00Z',
    deviceId: 'DEV-004',
    lat: 37.4381,
    lng: -122.1719,
  },
  {
    id: 'T-2851',
    organType: 'Kidney',
    destination: 'UCSF Medical Center',
    destLat: 37.7629,
    destLng: -122.4585,
    status: 'in_transit',
    temperature: 3.9,
    humidity: 51.2,
    tempStatus: 'ok',
    lastShock: { g: 0.5, at: '2025-02-14T08:50:00Z' },
    shockStatus: 'ok',
    startedAt: '2025-02-14T07:45:00Z',
    estimatedArrival: '2025-02-14T10:15:00Z',
    deviceId: 'DEV-005',
    lat: 37.7629,
    lng: -122.4585,
  },
  {
    id: 'T-2852',
    organType: 'Heart',
    destination: 'Kaiser Permanente',
    destLat: 37.7892,
    destLng: -122.4012,
    status: 'in_transit',
    temperature: 5.2,
    humidity: 47.8,
    tempStatus: 'ok',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T09:20:00Z',
    estimatedArrival: '2025-02-14T11:00:00Z',
    deviceId: 'DEV-006',
    lat: 37.7892,
    lng: -122.4012,
  },
  {
    id: 'T-2853',
    organType: 'Liver',
    destination: 'John Muir Health',
    destLat: 37.9256,
    destLng: -122.0567,
    status: 'in_transit',
    temperature: 4.8,
    humidity: 49.5,
    tempStatus: 'alert',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T08:00:00Z',
    estimatedArrival: '2025-02-14T10:45:00Z',
    deviceId: 'DEV-007',
    lat: 37.9256,
    lng: -122.0567,
  },
  {
    id: 'T-2854',
    organType: 'Kidney',
    destination: 'Sutter Health',
    destLat: 37.8012,
    destLng: -122.4356,
    status: 'in_transit',
    temperature: 6.1,
    humidity: 46.3,
    tempStatus: 'alert',
    lastShock: { g: 1.8, at: '2025-02-14T09:05:00Z' },
    shockStatus: 'warning',
    startedAt: '2025-02-14T07:30:00Z',
    estimatedArrival: '2025-02-14T11:30:00Z',
    deviceId: 'DEV-008',
    lat: 37.8012,
    lng: -122.4356,
  },
  {
    id: 'T-2855',
    organType: 'Lung',
    destination: 'Memorial Hospital',
    destLat: 37.7849,
    destLng: -122.4094,
    status: 'in_transit',
    temperature: 4.0,
    humidity: 55.0,
    tempStatus: 'ok',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T09:10:00Z',
    estimatedArrival: '2025-02-14T12:30:00Z',
    deviceId: 'DEV-009',
    lat: 37.7689,
    lng: -122.3891,
  },
  {
    id: 'T-2856',
    organType: 'Pancreas',
    destination: 'Regional Transplant Unit',
    destLat: 37.7699,
    destLng: -122.4294,
    status: 'in_transit',
    temperature: 5.5,
    humidity: 42.1,
    tempStatus: 'alert',
    lastShock: null,
    shockStatus: 'ok',
    startedAt: '2025-02-14T08:45:00Z',
    estimatedArrival: '2025-02-14T11:15:00Z',
    deviceId: 'DEV-010',
    lat: 37.7556,
    lng: -122.4123,
  },
];

export const currentReadings = {
  primaryDevice: {
    deviceId: 'DEV-001',
    temperature: 4.2,
    humidity: 45.4,
    temperatureHistory: [4.0, 4.1, 4.0, 4.2, 4.3, 4.2, 4.2],
    lastShock: null,
    shockHistory: [],
  },
  recentShocks: [
    { id: 1, deviceId: 'DEV-002', transportId: 'T-2848', gForce: 2.1, at: '2025-02-14T09:15:00Z', severity: 'warning' },
    { id: 2, deviceId: 'DEV-001', transportId: 'T-2847', gForce: 0.8, at: '2025-02-14T08:45:00Z', severity: 'ok' },
  ],
};

export const alerts = [
  { id: 1, type: 'temperature', severity: 'critical', message: 'T-2849 (Kidney): Temperature 7.8°C — outside safe range (0–4°C)', at: '2025-02-14T09:32:00Z' },
  { id: 2, type: 'shock', severity: 'warning', message: 'T-2848 (Liver): Shock event 2.1g detected', at: '2025-02-14T09:15:00Z' },
];
